import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import EagleSpinner from '../common/EagleSpinner';
import { FiCheckCircle } from 'react-icons/fi';

const MESSAGES = [
    "sprawdzamy jakie zadania musisz powtórzyć",
    "dobieramy zadania do Twojego ELO",
    "przepowiadamy, co CKE wymyśli w maju",
    "odkrywamy tajemnice arkuszy CKE",
    "dzwonimy do CKE i pytamy o Pewniaki",
    "optymalizujemy trudność pod Twój poziom ELO",
    "skanujemy wzorce z ostatnich matur",
    "liczymy Deltę wszędzie gdzie się da",
    "pomijamy zadania w Twojej pamięci długotrwałej",
    "sprawdzamy Twój poziom opanowania Pewniaków",
];

const MESSAGE_INTERVAL = 2200;
// Minimalny czas fazy ładowania zanim pokażemy "Gotowe!" — żeby na szybkich ładowaniach
// overlay nie migał. Paddujemy TYLKO szybkie fetch'e; wolne i tak trwają dłużej.
// Total min ≈ MIN_VISIBLE_MS + 500ms ("Gotowe!") ≈ 1,2s.
const MIN_VISIBLE_MS = 700;

// startAtFirst — dla komunikatów opowiadających sekwencję (np. przełączanie poziomu),
// gdzie losowy start psułby narrację. hint — stała linijka pod komunikatami.
const SessionSetupPopup = ({ isOpen, isDataReady, onComplete, messages, startAtFirst = false, hint }) => {
    const msgs = (messages && messages.length > 0) ? messages : MESSAGES;
    const [msgIndex, setMsgIndex] = useState(0);
    const [isDone, setIsDone] = useState(false);
    const intervalRef = useRef(null);
    const openedAtRef = useRef(0);

    useEffect(() => {
        if (!isOpen) {
            setMsgIndex(0);
            setIsDone(false);
            return;
        }
        openedAtRef.current = Date.now();
        setMsgIndex(startAtFirst ? 0 : Math.floor(Math.random() * msgs.length));
        intervalRef.current = setInterval(() => {
            setMsgIndex(prev => (prev + 1) % msgs.length);
        }, MESSAGE_INTERVAL);
        return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Dane gotowe: jeśli poszło szybciej niż MIN_VISIBLE_MS, dobij do minimum, potem "Gotowe!"
    useEffect(() => {
        if (!isDataReady) return;
        const elapsed = Date.now() - openedAtRef.current;
        const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
        const doneTimer = setTimeout(() => {
            clearInterval(intervalRef.current);
            setIsDone(true);
        }, wait);
        return () => clearTimeout(doneTimer);
    }, [isDataReady]);

    // Po "Gotowe!" — krótka pauza i finalizacja (nawigacja)
    useEffect(() => {
        if (!isDone) return;
        const timer = setTimeout(() => onComplete(), 500);
        return () => clearTimeout(timer);
    }, [isDone, onComplete]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <motion.div
            className="fixed inset-0 bg-white z-[9999] flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.12 }}
        >
            <EagleSpinner />

            <div className="relative h-12 mt-6 flex items-center justify-center w-full px-8">
                <AnimatePresence mode="wait">
                    {isDone ? (
                        <motion.div
                            key="done"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-2.5 text-[#007D00]"
                        >
                            <FiCheckCircle size={22} />
                            <span className="text-xl font-semibold">Gotowe!</span>
                        </motion.div>
                    ) : (
                        <motion.p
                            key={msgIndex}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.25 }}
                            className="text-lg text-gray-500 text-center leading-snug"
                        >
                            {msgs[msgIndex % msgs.length]}
                        </motion.p>
                    )}
                </AnimatePresence>
            </div>

            {hint && !isDone && (
                <p className="text-sm text-gray-400 text-center leading-snug px-8 max-w-[300px]">
                    {hint}
                </p>
            )}

            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gray-100">
                <motion.div
                    className="h-full bg-[#233855]/30"
                    initial={{ width: "0%" }}
                    animate={{ width: isDone ? "100%" : "82%" }}
                    transition={isDone
                        ? { duration: 0.25, ease: "easeOut" }
                        : { duration: 7, ease: [0.2, 0.05, 0.1, 1] }
                    }
                />
            </div>
        </motion.div>,
        document.body
    );
};

export default SessionSetupPopup;
